import React from 'react';
import { MicIcon, FileTextIcon, UsersIcon, ClipboardCheckIcon, AlertTriangleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function Screen11_PTPCreated({
  data,
  onNext,
  onBack
}: ScreenProps) {
  return <div className="flex flex-col h-full" data-id="element-890">
      <div className="mb-5" data-id="element-891">
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-892">
          Start PTP Meeting
        </h2>
        <p className="text-gray-500 text-sm" data-id="element-893">
          Gather your crew and record the pre-task briefing. AI will analyze the
          discussion in real time.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-894">
        {/* PTP Info Card */}
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-895">
          <div className="flex items-center gap-2 mb-3" data-id="element-896">
            <ClipboardCheckIcon className="w-4 h-4 text-green-600" data-id="element-897" />
            <span className="text-xs font-bold text-green-600 uppercase tracking-wider" data-id="element-898">
              PTP Ready
            </span>
            <span className="text-gray-400 text-xs ml-auto" data-id="element-899">{data.date}</span>
          </div>
          <p className="text-yellow-500 font-bold text-sm mb-2" data-id="element-900">
            {data.taskTitle || 'Pre-Task Plan'}
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-901">
            <span data-id="element-902">{data.project}</span>
            <span data-id="element-903">•</span>
            <span data-id="element-904">{data.location}</span>
          </div>
        </div>

        {/* Crew Checklist */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4" data-id="element-905">
          <div className="flex items-center gap-2 mb-3" data-id="element-906">
            <UsersIcon className="w-4 h-4 text-gray-400" data-id="element-907" />
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-908">
              Crew Present ({data.crewSize})
            </h3>
          </div>
          <div className="flex flex-wrap gap-1.5" data-id="element-909">
            {data.crewRoles.map((role, i) => <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 border border-gray-200 text-gray-700" data-id="element-910">
                <span className="font-semibold" data-id="element-911">{role.name}</span>
                <span className="text-gray-400" data-id="element-912">·</span>
                <span className="text-gray-400" data-id="element-913">{role.role}</span>
              </span>)}
          </div>
        </div>

        {/* Meeting Tips */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4" data-id="element-914">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-915">
            Meeting Checklist
          </h3>
          <ul className="space-y-2.5" data-id="element-916">
            {['Review each job step and associated hazards', 'Discuss controls and PPE requirements', 'Confirm crew roles and responsibilities', 'Ask if anyone has safety concerns', 'Verify everyone understands the plan'].map((item, i) => <motion.li key={i} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1 + i * 0.08
          }} className="flex items-start gap-2.5" data-id="element-917">
                <div className="w-4 h-4 rounded border border-gray-200 bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-918">
                  <span className="text-[8px] text-gray-400 font-bold" data-id="element-919">
                    {i + 1}
                  </span>
                </div>
                <span className="text-gray-700 text-sm leading-snug" data-id="element-920">
                  {item}
                </span>
              </motion.li>)}
          </ul>
        </div>

        {/* Recording Note */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2.5" data-id="element-921">
          <AlertTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-922" />
          <p className="text-amber-700 text-xs leading-relaxed" data-id="element-923">
            Make sure all crew members are present before starting. The AI will
            detect speakers and track topic coverage during the recording.
          </p>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-3 bg-gray-50" data-id="element-924">
        <PrimaryButton onClick={onNext} icon={<MicIcon className="w-5 h-5" data-id="element-926" />} data-id="element-925">
          Start Recording
        </PrimaryButton>
        <SecondaryButton onClick={onBack} icon={<FileTextIcon className="w-5 h-5" data-id="element-928" />} data-id="element-927">
          Review PTP
        </SecondaryButton>
      </div>
    </div>;
}